import React from "react";
import {
  Table,
  TableColumn,
  TableRowKey,
  Button,
  ButtonGroup,
} from "@datapay-by-datacom/design-system";
import { UseQueryResult } from "@tanstack/react-query";
import { PendingOrgInviteBulk } from "../../../hooks/OrgInvite/OrgInviteBulk/useOrgInviteBulkCsvParser";
import styles from "./OrgInviteBulkTable.module.css";

type OrgInviteBulkTableData = PendingOrgInviteBulk & {
  actions: string;
  __index: number;
};

interface OrgInviteBulkTableProps {
  pendingInvites: PendingOrgInviteBulk[];
  onRemoveInvite: (index: number) => void;
  onClearAll: () => void;
  onSendInvitations: () => void;
}

const rowKey: TableRowKey<OrgInviteBulkTableData> = (row) =>
  `${row.email}-${row.__index}`;

export function OrgInviteBulkTable({
  pendingInvites,
  onRemoveInvite,
  onClearAll,
  onSendInvitations,
}: OrgInviteBulkTableProps) {
  const tableColumns: TableColumn<OrgInviteBulkTableData>[] = [
    {
      key: "name",
      cell(row) {
        return row.name;
      },
      header() {
        return "Name";
      },
    },
    {
      key: "email",
      cell(row) {
        return row.email;
      },
      header() {
        return "Email";
      },
    },
    {
      key: "role",
      cell(row) {
        return row.role;
      },
      header() {
        return "Role";
      },
    },
    {
      key: "actions",
      cell(row) {
        return (
          <Button
            variant="tertiary"
            onPress={() => onRemoveInvite(row.__index)}
          >
            Remove
          </Button>
        );
      },
      header() {
        return "Actions";
      },
    },
  ];

  const tableData: OrgInviteBulkTableData[] = pendingInvites.map(
    (invite, index) => ({
      ...invite,
      actions: "",
      __index: index,
    })
  );

  if (pendingInvites.length === 0) {
    return null;
  }

  return (
    <div className={styles.tableContainer}>
      <div className={styles.tableHeader}>
        <h3>Pending Bulk Invitations ({pendingInvites.length})</h3>
        <ButtonGroup>
          <Button variant="tertiary" onPress={onClearAll}>
            Clear All
          </Button>
          <Button variant="primary" onPress={onSendInvitations}>
            Send Invitations
          </Button>
        </ButtonGroup>
      </div>

      <Table
        description="Pending Bulk Invitations"
        config={{
          columns: tableColumns,
          rowKey,
        }}
        control={{ params: {}, onParamsChange: () => {} }}
        query={
          {
            data: tableData,
            isLoading: false,
            error: null,
          } as UseQueryResult<OrgInviteBulkTableData[]>
        }
      />
    </div>
  );
}
